import { formatCountdown, remainingAt, type TimerState } from '../../lib/timers';

/**
 * Countdown controls for one timed step. The visual state carries the alarm as
 * well as the sound: a finished timer flashes, so a muted phone in a noisy
 * kitchen still tells you.
 */
export function StepTimer({
  stepId,
  label,
  durationMs,
  timer,
  now,
  onStart,
  onPause,
  onResume,
  onReset,
}: {
  stepId: string;
  /** What the timer is for, e.g. "Baste the turkey" — used for the button labels. */
  label: string;
  durationMs: number;
  timer: TimerState | undefined;
  now: number;
  onStart: (stepId: string, durationMs: number) => void;
  onPause: (stepId: string) => void;
  onResume: (stepId: string) => void;
  onReset: (stepId: string) => void;
}) {
  const status = timer?.status ?? 'idle';
  const remaining = timer ? remainingAt(timer, now) : durationMs;
  const done = status === 'done';
  const buttonClass =
    'min-h-11 rounded-lg border border-current px-3 text-sm font-semibold disabled:opacity-50';

  return (
    <div className="flex flex-wrap items-center gap-2">
      <span
        aria-live={done ? 'assertive' : 'off'}
        className={`min-w-20 rounded-lg px-2 py-1 text-center text-xl font-bold tabular-nums ${
          done ? 'animate-pulse bg-orange text-ink' : 'bg-surface/70'
        }`}
      >
        {done ? 'Time!' : formatCountdown(remaining)}
      </span>

      {status === 'idle' || status === 'done' ? (
        <button
          type="button"
          className={buttonClass}
          onClick={() => onStart(stepId, durationMs)}
          aria-label={done ? `Restart timer for ${label}` : `Start timer for ${label}`}
        >
          {done ? 'Restart' : 'Start timer'}
        </button>
      ) : null}

      {status === 'running' ? (
        <button
          type="button"
          className={buttonClass}
          onClick={() => onPause(stepId)}
          aria-label={`Pause timer for ${label}`}
        >
          Pause
        </button>
      ) : null}

      {status === 'paused' ? (
        <button
          type="button"
          className={buttonClass}
          onClick={() => onResume(stepId)}
          aria-label={`Resume timer for ${label}`}
        >
          Resume
        </button>
      ) : null}

      {status !== 'idle' ? (
        <button
          type="button"
          className={buttonClass}
          onClick={() => onReset(stepId)}
          aria-label={`Reset timer for ${label}`}
        >
          Reset
        </button>
      ) : null}
    </div>
  );
}
